import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';

type VenueLocale = Readonly<{ locale: string; currency: CurrencyCodeType }>;

// en-IE / en-NZ have no bundle of their own; event_service aliases them to en-GB / en-AU on write.
const ENGLISH_VENUE_COUNTRY_LOCALES: Readonly<Record<string, VenueLocale>> = {
  US: { locale: 'en-US', currency: 'USD' },
  GB: { locale: 'en-GB', currency: 'GBP' },
  IE: { locale: 'en-IE', currency: 'EUR' },
  AU: { locale: 'en-AU', currency: 'AUD' },
  NZ: { locale: 'en-NZ', currency: 'NZD' },
  CA: { locale: 'en-CA', currency: 'CAD' },
};

type ResolveVenueLocaleArgs = Readonly<{
  /** Locale resolved from URL, stored language, or browser (`useGeoBasedLocale`). */
  defaultLocale: string;
  defaultCurrency: CurrencyCodeType;
  venueCountryCode: string | null | undefined;
  /** The couple chose a language explicitly (e.g. `?lang=` / `?culture=`), so the venue must not override it. */
  hasExplicitLanguageChoice: boolean;
  /** The URL carries a currency or region (`?currency=`, `?lang=`, `?culture=`), so the venue must not override the currency. */
  hasExplicitCurrencyChoice: boolean;
}>;

/**
 * The venue's country decides an English event's regional locale and currency, over a stored or
 * browser default that may be stale. Non-English defaults are kept: the venue says nothing about
 * which language the couple reads.
 */
export const resolveVenueLocale = ({
  defaultLocale,
  defaultCurrency,
  venueCountryCode,
  hasExplicitLanguageChoice,
  hasExplicitCurrencyChoice,
}: ResolveVenueLocaleArgs): VenueLocale => {
  const isEnglishDefault = defaultLocale.toLowerCase().split('-')[0] === 'en';
  const venueLocale = venueCountryCode && isEnglishDefault ? ENGLISH_VENUE_COUNTRY_LOCALES[venueCountryCode.toUpperCase()] : undefined;
  if (!venueLocale) return { locale: defaultLocale, currency: defaultCurrency };

  return {
    locale: hasExplicitLanguageChoice ? defaultLocale : venueLocale.locale,
    currency: hasExplicitCurrencyChoice ? defaultCurrency : venueLocale.currency,
  };
};
